import type { Formato } from '../../design/graficos/modelos';
import type {
  IndicadorDaEquipe,
  MetaDoIndicador,
  PessoaNasMetas,
  PorDoGrafico,
} from '../../escala';

/**
 * A conta da aba Metas, SEM tela (testada à parte): períodos, ranking e comparação com a meta.
 * Só dado real: sem valor é "sem dado" (null), nunca zero; pessoa sem conta fica fora do ranking.
 */

export type PresetDePeriodo =
  'semana' | 'semana_passada' | 'mes' | 'mes_passado' | 'trinta' | 'livre';

export const PRESETS: readonly [PresetDePeriodo, string][] = [
  ['semana', 'Esta semana'],
  ['semana_passada', 'Semana passada'],
  ['mes', 'Este mês'],
  ['mes_passado', 'Mês passado'],
  ['trinta', 'Últimos 30 dias'],
  ['livre', 'Período livre'],
];

const somar = (dia: string, n: number) =>
  new Date(Date.parse(`${dia}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** Segunda-feira da semana do dia (semana = segunda a domingo). */
export function segundaDaSemana(dia: string): string {
  const dow = new Date(`${dia}T12:00:00Z`).getUTCDay();
  return somar(dia, -((dow + 6) % 7));
}

/** O período de cada atalho. A semana e o mês em curso vão até hoje; os passados, até o fim. */
export function periodoDoPreset(
  preset: Exclude<PresetDePeriodo, 'livre'>,
  hoje: string,
): { de: string; ate: string } {
  if (preset === 'semana') return { de: segundaDaSemana(hoje), ate: hoje };
  if (preset === 'semana_passada') {
    const seg = somar(segundaDaSemana(hoje), -7);
    return { de: seg, ate: somar(seg, 6) };
  }
  if (preset === 'mes') return { de: `${hoje.slice(0, 8)}01`, ate: hoje };
  if (preset === 'mes_passado') {
    const [a, m] = hoje.split('-').map(Number);
    const de = new Date(Date.UTC(a ?? 2000, (m ?? 1) - 2, 1)).toISOString().slice(0, 10);
    const ate = new Date(Date.UTC(a ?? 2000, (m ?? 1) - 1, 0)).toISOString().slice(0, 10);
    return { de, ate };
  }
  return { de: somar(hoje, -29), ate: hoje };
}

/** Como agrupar o gráfico quando a pessoa não escolheu: até 31 dias, dia a dia; até 120, semana. */
export function agrupamentoSugerido(de: string, ate: string): PorDoGrafico {
  const dias =
    Math.round((Date.parse(`${ate}T12:00:00Z`) - Date.parse(`${de}T12:00:00Z`)) / 86_400_000) + 1;
  return dias <= 31 ? 'dia' : dias <= 120 ? 'semana' : 'mes';
}

export const formatoDoIndicador = (i: Pick<IndicadorDaEquipe, 'unidade' | 'casas'>): Formato =>
  i.unidade === 'RS' ? 'reais' : i.unidade === 'PCT' ? 'pct' : i.casas > 0 ? 'decimal' : 'inteiro';

/** Variação % contra o período anterior (null se não dá para comparar). */
export function variacaoPct(atual: number | null, anterior: number | null): number | null {
  if (atual === null || anterior === null || anterior === 0) return null;
  return ((atual - anterior) / Math.abs(anterior)) * 100;
}

export type SituacaoDaMeta = 'sem_dado' | 'sem_meta' | 'bateu' | 'perto' | 'abaixo';

/**
 * Valor × meta (alvo do 1º nível). MAIOR_MELHOR: bateu se valor ≥ alvo; MENOR_MELHOR (desconto):
 * bateu se valor ≤ alvo. "perto" = dentro de 10% do alvo. `pct` = quanto da meta já foi (0–∞).
 */
export function situacaoDaMeta(
  valor: number | null,
  meta: Pick<MetaDoIndicador, 'alvo'> | undefined,
  direcao: IndicadorDaEquipe['direcao'],
): { situacao: SituacaoDaMeta; pct: number | null } {
  if (valor === null) return { situacao: 'sem_dado', pct: null };
  if (!meta) return { situacao: 'sem_meta', pct: null };
  const sobe = direcao === 'MAIOR_MELHOR';
  const bateu = sobe ? valor >= meta.alvo : valor <= meta.alvo;
  const pct = meta.alvo === 0 ? null : sobe ? (valor / meta.alvo) * 100 : (meta.alvo / valor) * 100;
  if (bateu) return { situacao: 'bateu', pct };
  const folga = meta.alvo === 0 ? 0 : Math.abs(valor - meta.alvo) / Math.abs(meta.alvo);
  return { situacao: folga <= 0.1 ? 'perto' : 'abaixo', pct: pct === null ? null : pct };
}

export interface LinhaDoRanking {
  readonly pessoa: PessoaNasMetas;
  readonly valor: number;
  readonly meta: MetaDoIndicador | undefined;
  readonly posicao: number;
}

/**
 * Ranking da equipe num indicador: melhor primeiro (maior, ou menor no desconto). Fica de fora
 * quem não tem conta ligada ou não tem dado no período — nunca entra como zero.
 */
export function rankingDoIndicador(
  pessoas: readonly PessoaNasMetas[],
  ind: Pick<IndicadorDaEquipe, 'codigo' | 'direcao'>,
): LinhaDoRanking[] {
  const com = pessoas.flatMap((p) => {
    const v = p.semConta ? null : (p.valores[ind.codigo] ?? null);
    return v === null ? [] : [{ pessoa: p, valor: v, meta: p.metas[ind.codigo] }];
  });
  com.sort(
    (a, b) =>
      (ind.direcao === 'MAIOR_MELHOR' ? b.valor - a.valor : a.valor - b.valor) ||
      a.pessoa.nome.localeCompare(b.pessoa.nome, 'pt-BR'),
  );
  return com.map((x, i) => ({ ...x, posicao: i + 1 }));
}

/** Quem está fora do ranking, e por quê (para a tela dizer, não esconder). */
export function foraDoRanking(
  pessoas: readonly PessoaNasMetas[],
  codigo: string,
): { semConta: string[]; semDado: string[] } {
  return {
    semConta: pessoas.filter((p) => p.semConta).map((p) => p.nome),
    semDado: pessoas
      .filter((p) => !p.semConta && (p.valores[codigo] ?? null) === null)
      .map((p) => p.nome),
  };
}
